import { memo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EndgameResult, EndgameType, Color, typedEmit } from '@fog-chess/core';
import { cn } from '@/lib/utils';

export interface EndgameModalProps {
  /** Endgame result from server (game:over event) */
  result: EndgameResult | null;
  /** Viewer's color */
  viewerColor: Color | null;
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback when user closes modal */
  onClose: () => void;
  /** Current viewer's rating (for delta display) */
  viewerRating?: number;
}

/**
 * Endgame Modal — Shows game result with Truth Board option.
 * 
 * Triggered by `game:over` socket event.
 * Displays:
 * - Winner and reason (EndgameType)
 * - FTPES Rating Delta
 * - "View Truth Board" button that rehydrates board using unmasked truth_fen
 */
export const EndgameModal = memo(function EndgameModal({
  result,
  viewerColor,
  isOpen,
  onClose,
  viewerRating,
}: EndgameModalProps) {
  // Auto-focus close button on open
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      const closeBtn = document.querySelector('[data-endgame-close]') as HTMLElement;
      closeBtn?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!result || !isOpen) return null;

  const { type, winner_color, move_history_uci, ply_count } = result;
  
  // Determine result text and styling
  const isDraw = [
    'stalemate',
    'insufficient_material',
    'threefold_repetition',
    'fifty_move_rule',
    'fivefold_repetition',
    'seventy_five_move_rule',
    'draw_agreement',
    'draw_claim',
  ].includes(type);
  
  const isWin = !isDraw && winner_color === viewerColor;
  const isLoss = !isDraw && winner_color !== viewerColor && winner_color !== undefined;
  
  // Format endgame type for display
  const formatEndgameType = (type: EndgameType): string => {
    const labels: Record<EndgameType, string> = {
      checkmate: 'Checkmate',
      stalemate: 'Stalemate',
      insufficient_material: 'Insufficient Material',
      threefold_repetition: 'Threefold Repetition',
      fifty_move_rule: '50-Move Rule',
      resignation: 'Resignation',
      disconnection_timeout: 'Disconnection Timeout',
      turn_timeout: 'Turn Timeout',
      fivefold_repetition: 'Fivefold Repetition',
      seventy_five_move_rule: '75-Move Rule',
      draw_agreement: 'Draw Agreement',
      draw_claim: 'Draw Claim',
    };
    return labels[type] || type;
  };
  
  // Calculate rating delta (would come from server in real implementation)
  const ratingDelta = viewerRating ? 0 : null; // Server should provide this
  
  // Handle "View Truth Board" - emits spectate:join to get unmasked truth_fen
  const handleViewTruthBoard = () => {
    // Emit spectate:join with view=truth to get unmasked board
    // The server will send back spectate:state with TruthViewPayload
    console.log('[EndgameModal] Requesting Truth Board view');
    // The matchId would need to be passed in or retrieved from store
    typedEmit('spectate:join', { 
      match_id: '', // Will be filled by store
      room_code: '' 
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent 
            className={cn(
              'max-w-lg overflow-hidden',
              isWin ? 'border-green-500/30' : isLoss ? 'border-red-500/30' : 'border-amber-500/30'
            )}
          >
            {/* Animated background */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/30"
            />
            
            {/* Header with result */}
            <DialogHeader className="relative z-10 text-center pb-6">
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="inline-flex items-center gap-3 p-4 rounded-2xl mb-4"
                style={{
                  background: isWin 
                    ? 'linear-gradient(135deg, hsl(var(--green)/15), hsl(var(--green)/5))'
                    : isLoss 
                    ? 'linear-gradient(135deg, hsl(var(--destructive)/15), hsl(var(--destructive)/5))'
                    : 'linear-gradient(135deg, hsl(var(--amber)/15), hsl(var(--amber)/5))',
                }}
              >
                {isWin && (
                  <motion.svg
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, duration: 0.5, ease: 'easeOut' }}
                    className="h-10 w-10 text-green-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </motion.svg>
                )}
                {isLoss && (
                  <motion.svg
                    initial={{ scale: 0, rotate: 180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, duration: 0.5, ease: 'easeOut' }}
                    className="h-10 w-10 text-red-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </motion.svg>
                )}
                {!isDraw && !isWin && !isLoss && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
                    className="h-10 w-10 text-amber-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </motion.svg>
                )}
                <div>
                  <DialogTitle className="text-2xl font-bold">
                    {isWin ? 'Victory!' : isLoss ? 'Defeat' : 'Draw'}
                  </DialogTitle>
                  <DialogDescription className="text-lg mt-1">
                    {formatEndgameType(type)}
                  </DialogDescription>
                </div>
              </motion.div>
              
              {/* Winner info */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
                className="flex items-center justify-center gap-3"
              >
                {winner_color && (
                  <>
                    <Badge variant="outline" className="text-sm">
                      Winner: {winner_color === 'white' ? 'White' : 'Black'}
                    </Badge>
                    {viewerColor === winner_color && (
                      <Badge variant="default" className="text-sm">
                        You Won
                      </Badge>
                    )}
                    {viewerColor && viewerColor !== winner_color && !isDraw && (
                      <Badge variant="destructive" className="text-sm">
                        You Lost
                      </Badge>
                    )}
                    {isDraw && (
                      <Badge variant="secondary" className="text-sm">
                        Draw
                      </Badge>
                    )}
                  </>
                )}
              </motion.div>
            </DialogHeader>
            
            {/* Game stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="relative z-10 grid grid-cols-3 gap-4 p-4 mb-4 rounded-xl bg-muted/30"
            >
              <div className="text-center">
                <p className="text-2xl font-bold font-mono tabular-nums text-foreground">
                  {ply_count}
                </p>
                <p className="text-xs text-muted-foreground">Total Plies</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold font-mono tabular-nums text-foreground">
                  {move_history_uci.length}
                </p>
                <p className="text-xs text-muted-foreground">Moves</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold font-mono tabular-nums 
                  {ratingDelta !== null ? (ratingDelta > 0 ? 'text-green-500' : ratingDelta < 0 ? 'text-red-500' : '') : ''}
                ">
                  {ratingDelta !== null 
                    ? (ratingDelta > 0 ? `+${ratingDelta}` : `${ratingDelta}`)
                    : '—'
                  }
                </p>
                <p className="text-xs text-muted-foreground">Rating Δ</p>
              </div>
            </motion.div>
            
            {/* Rating delta detail (if available) */}
            {ratingDelta !== null && viewerRating && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ delay: 0.25, duration: 0.3 }}
                className="relative z-10 p-4 mb-4 rounded-xl bg-primary/10 border border-primary/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">FTPES Rating Change</span>
                    <Badge variant={ratingDelta > 0 ? 'default' : ratingDelta < 0 ? 'destructive' : 'secondary'}>
                      {ratingDelta > 0 ? '+' : ''}{ratingDelta}
                    </Badge>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-muted-foreground">New Rating</p>
                    <p className="font-bold font-mono">{viewerRating + ratingDelta}</p>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Actions */}
            <DialogFooter className="relative z-10 flex-col gap-3 p-4">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleViewTruthBoard}
                className="w-full"
              >
                <Button size="lg" className="w-full gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View Truth Board
                </Button>
              </motion.button>
              
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                data-endgame-close
                className="w-full"
              >
                <Button variant="ghost" size="lg" className="w-full">
                  Close
                </Button>
              </motion.button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
});

EndgameModal.displayName = 'EndgameModal';